import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert, Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Grid, Paper,
  Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  createFlow, deleteFlow, duplicateFlow, getFlowAnalytics, getFlowRuns, getFlows,
  publishFlow, unpublishFlow
} from '../services/flowBuilder.service';
import { normalizeKeywords } from '../components/flow-builder/flowBuilderConfig';
import WhatsAppAccountSelect from '../components/WhatsAppAccountSelect';
import FacebookPageSelect from '../components/FacebookPageSelect';

const CREATE_CHANNEL_OPTIONS = [
  ['whatsapp', 'WhatsApp'],
  ['facebook_messenger', 'Facebook Messenger'],
  ['facebook_comment', 'Facebook Comments']
];

function FlowAnalyticsDialog({ flow, onClose }) {
  const [analytics, setAnalytics] = useState(null);
  const [runs, setRuns] = useState([]);
  const [errorRun, setErrorRun] = useState(null);

  useEffect(() => {
    if (!flow) return;
    Promise.all([getFlowAnalytics(flow.id), getFlowRuns(flow.id)]).then(([analyticsRes, runsRes]) => {
      setAnalytics(analyticsRes.data.data);
      setRuns(runsRes.data.data || []);
    });
  }, [flow]);

  return (
    <Dialog open={!!flow} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Flow Analytics: {flow?.name}</DialogTitle>
      <DialogContent>
        {analytics && (
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {[
              ['Total Executions', analytics.totalExecutions],
              ['Completed', analytics.completed],
              ['Failed', analytics.failed],
              ['Running', analytics.running]
            ].map(([label, value]) => (
              <Grid item xs={6} md={3} key={label}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="h5" fontWeight={850}>{value}</Typography>
                  <Typography color="text.secondary">{label}</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        )}
        <Typography variant="h6" fontWeight={850} sx={{ mb: 1 }}>Recent Runs</Typography>
        <TableContainer>
          <Table size="small">
            <TableHead><TableRow><TableCell>ID</TableCell><TableCell>Status</TableCell><TableCell>Current Node</TableCell><TableCell>Started</TableCell><TableCell align="right">Error</TableCell></TableRow></TableHead>
            <TableBody>{runs.map((run) => <TableRow key={run.id}><TableCell>{run.id}</TableCell><TableCell>{run.status}</TableCell><TableCell>{run.currentNodeKey || '-'}</TableCell><TableCell>{run.startedAt ? new Date(run.startedAt).toLocaleString() : '-'}</TableCell><TableCell align="right">{run.status === 'failed' && <Button size="small" onClick={() => setErrorRun(run)}>View Error</Button>}</TableCell></TableRow>)}</TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
      <Dialog open={!!errorRun} onClose={() => setErrorRun(null)} maxWidth="md" fullWidth>
        <DialogTitle>Flow Run Error</DialogTitle>
        <DialogContent>
          {errorRun && <Stack spacing={1.5}>
            <Typography><strong>Run ID:</strong> {errorRun.id}</Typography>
            <Typography><strong>Status:</strong> {errorRun.status}</Typography>
            <Typography><strong>Failed Node:</strong> {errorRun.failedNodeId || errorRun.currentNodeKey || '-'}</Typography>
            <Typography><strong>Node Type:</strong> {errorRun.failedNodeType || '-'}</Typography>
            <Typography><strong>Error Message:</strong> {errorRun.errorMessage || '-'}</Typography>
            <Typography fontWeight={800}>WhatsApp API Response</Typography>
            <Paper variant="outlined" sx={{ p: 1.5, maxHeight: 220, overflow: 'auto', bgcolor: '#fafafa' }}><pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{JSON.stringify(errorRun.whatsappApiResponse || {}, null, 2)}</pre></Paper>
            <Typography fontWeight={800}>Payload Sent</Typography>
            <Paper variant="outlined" sx={{ p: 1.5, maxHeight: 220, overflow: 'auto', bgcolor: '#fafafa' }}><pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{JSON.stringify(errorRun.payloadSent || {}, null, 2)}</pre></Paper>
          </Stack>}
        </DialogContent>
        <DialogActions><Button onClick={() => setErrorRun(null)}>Close</Button></DialogActions>
      </Dialog>
    </Dialog>
  );
}

function FlowBuilderListPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const whatsappAccountId = searchParams.get('whatsappAccountId') || '';
  const setWhatsappAccountId = (value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set('whatsappAccountId', value); else next.delete('whatsappAccountId');
    setSearchParams(next, { replace: true });
  };
  const [flows, setFlows] = useState([]);
  const [filterAccounts, setFilterAccounts] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ name: 'New WhatsApp Flow', description: '', triggerKeywords: 'start', channels: ['whatsapp'], whatsappAccountId: '', facebookPageId: '' });
  const toggleCreateChannel = (value) => {
    setForm((current) => {
      const next = current.channels.includes(value) ? current.channels.filter((item) => item !== value) : [...current.channels, value];
      return next.length ? { ...current, channels: next } : current; // at least one channel must stay selected
    });
  };
  const [analyticsFlow, setAnalyticsFlow] = useState(null);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  // Stabilized so the effect below can depend on it directly (satisfying
  // react-hooks/exhaustive-deps genuinely, with no disable comment) — its
  // identity only changes when whatsappAccountId does, which is exactly
  // when a re-fetch should happen. publish()/duplicate()/remove() below
  // also call this same function to refresh after their own action.
  const load = useCallback(async () => {
    const res = await getFlows(whatsappAccountId ? { whatsappAccountId } : {});
    setFlows(res.data.data || []);
  }, [whatsappAccountId]);

  useEffect(() => {
    setLoading(true);
    load().catch((err) => setError(err.response?.data?.message || 'Unable to load flows.')).finally(() => setLoading(false));
  }, [load]);

  // A flow's own WhatsApp/Facebook channel membership, mirroring the
  // backend's flowChannelCompatibility.flowChannels() exactly, so the
  // "WhatsApp number" column never mislabels a Facebook-only flow as a
  // global/default WhatsApp flow just because both leave whatsappAccountId null.
  const flowChannels = (flow) => (Array.isArray(flow.channels) && flow.channels.length ? flow.channels : [flow.channel || 'whatsapp']);
  // Resolved from the SAME accounts already fetched for the filter dropdown
  // below (via onAccountsLoaded) — no separate per-row/N+1 lookup.
  const accountLabel = useMemo(() => {
    const byId = new Map(filterAccounts.map((account) => [String(account.id), account]));
    return (flow) => {
      if (flow.whatsappAccountId) {
        const account = byId.get(String(flow.whatsappAccountId));
        return account ? `${account.name}${account.phoneNumber ? ` · ${account.phoneNumber}` : ''}` : `Account #${flow.whatsappAccountId}`;
      }
      return flowChannels(flow).includes('whatsapp') ? 'All numbers (default)' : '—';
    };
  }, [filterAccounts]);

  const create = async () => {
    const keywords = normalizeKeywords(form.triggerKeywords);
    const res = await createFlow({
      name: form.name,
      description: form.description,
      channels: form.channels,
      whatsappAccountId: form.channels.includes('whatsapp') ? (form.whatsappAccountId || undefined) : undefined,
      facebookPageId: form.channels.some((channel) => channel !== 'whatsapp') ? (form.facebookPageId || undefined) : undefined,
      triggerType: 'inbound_message',
      triggerKeywords: keywords,
      triggerConfig: { source: 'inbound_message', keywords, matchType: 'contains', keywordMatchMode: 'contains' }
    });
    setCreateOpen(false);
    navigate(`/flow-builder/${res.data.data.id}`);
  };

  const publish = async (flow) => {
    if (flow.status === 'published') await unpublishFlow(flow.id);
    else await publishFlow(flow.id);
    setNotice(flow.status === 'published' ? 'Flow unpublished.' : 'Flow published.');
    await load();
  };

  const duplicate = async (flow) => {
    await duplicateFlow(flow.id);
    setNotice('Flow duplicated.');
    await load();
  };

  const remove = async (flow) => {
    await deleteFlow(flow.id);
    setNotice('Flow deleted.');
    await load();
  };

  return (
    <Stack spacing={2.5}>
      {notice && <Alert severity="success" onClose={() => setNotice('')}>{notice}</Alert>}
      {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}
      <Paper sx={{ p: 2.5, border: '1px solid', borderColor: 'divider' }} elevation={0}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" fontWeight={850}>WhatsApp Flow Builder</Typography>
            <Typography color="text.secondary">Build ManyChat-style visual automations for WhatsApp conversations.</Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              // Preselect the currently-filtered number where technically
              // appropriate (a WhatsApp-channel create); the agent can still
              // change it (or pick none) if their permissions allow other numbers.
              setForm((current) => ({ ...current, whatsappAccountId: current.channels.includes('whatsapp') ? whatsappAccountId : '' }));
              setCreateOpen(true);
            }}
          >Create Flow</Button>
        </Stack>
      </Paper>

      <Paper sx={{ p: 2, border: '1px solid', borderColor: 'divider' }} elevation={0}>
        <Box sx={{ maxWidth: 320 }}>
          <WhatsAppAccountSelect
            label="WhatsApp Number"
            value={whatsappAccountId}
            onChange={setWhatsappAccountId}
            onAccountsLoaded={setFilterAccounts}
            allowAll
            fullWidth
          />
        </Box>
      </Paper>

      <Paper sx={{ border: '1px solid', borderColor: 'divider', overflow: 'hidden' }} elevation={0}>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table>
            <TableHead><TableRow><TableCell>Name</TableCell><TableCell>Trigger</TableCell><TableCell>WhatsApp number</TableCell><TableCell>Status</TableCell><TableCell>Updated</TableCell><TableCell>Runs</TableCell><TableCell align="right">Actions</TableCell></TableRow></TableHead>
            <TableBody>
              {flows.map((flow) => (
                <TableRow key={flow.id} hover>
                  <TableCell><Typography fontWeight={850}>{flow.name}</Typography><Typography variant="body2" color="text.secondary">{flow.description || '-'}</Typography></TableCell>
                  <TableCell>{(flow.triggerKeywords || []).join(', ') || '-'}</TableCell>
                  <TableCell>{accountLabel(flow)}</TableCell>
                  <TableCell><Chip size="small" label={flow.status} color={flow.status === 'published' ? 'success' : 'default'} /></TableCell>
                  <TableCell>{flow.updatedAt ? new Date(flow.updatedAt).toLocaleString() : '-'}</TableCell>
                  <TableCell>{flow.executions || 0}</TableCell>
                  <TableCell align="right">
                    <Button size="small" startIcon={<EditIcon />} component={Link} to={`/flow-builder/${flow.id}`}>Edit</Button>
                    <Button size="small" startIcon={<ContentCopyIcon />} onClick={() => duplicate(flow)}>Duplicate</Button>
                    <Button size="small" startIcon={<RocketLaunchIcon />} onClick={() => publish(flow)}>{flow.status === 'published' ? 'Unpublish' : 'Publish'}</Button>
                    <Button size="small" startIcon={<AnalyticsIcon />} onClick={() => setAnalyticsFlow(flow)}>Analytics</Button>
                    <Button size="small" color="error" startIcon={<DeleteIcon />} onClick={() => remove(flow)}>Delete</Button>
                  </TableCell>
                </TableRow>
              ))}
              {!loading && flows.length === 0 && <TableRow><TableCell colSpan={7}><Typography sx={{ py: 4, textAlign: 'center' }} color="text.secondary">{whatsappAccountId ? 'No flows for this WhatsApp number.' : 'No flows yet. Create your first WhatsApp automation.'}</Typography></TableCell></TableRow>}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Flow</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Flow Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} fullWidth />
            <TextField label="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} fullWidth />
            <TextField label="Trigger Keywords" value={form.triggerKeywords} onChange={(e) => setForm({ ...form, triggerKeywords: e.target.value })} helperText="Comma-separated keywords, for example: start, forex, course" fullWidth />
            <Stack spacing={0.5}>
              <Typography variant="caption" fontWeight={800} color="text.secondary">Channels</Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {CREATE_CHANNEL_OPTIONS.map(([value, label]) => (
                  <FormControlLabel
                    key={value}
                    control={<Checkbox size="small" checked={form.channels.includes(value)} onChange={() => toggleCreateChannel(value)} />}
                    label={<Typography fontSize={13}>{label}</Typography>}
                  />
                ))}
              </Stack>
            </Stack>
            {form.channels.includes('whatsapp') && <WhatsAppAccountSelect value={form.whatsappAccountId} onChange={(value) => setForm({ ...form, whatsappAccountId: value || '' })} allowAll fullWidth />}
            {form.channels.some((channel) => channel !== 'whatsapp') && <FacebookPageSelect value={form.facebookPageId} onChange={(value) => setForm({ ...form, facebookPageId: value || '' })} required fullWidth />}
          </Stack>
        </DialogContent>
        <DialogActions><Button onClick={() => setCreateOpen(false)}>Cancel</Button><Button variant="contained" startIcon={<PlayArrowIcon />} onClick={create}>Create & Open</Button></DialogActions>
      </Dialog>
      <FlowAnalyticsDialog flow={analyticsFlow} onClose={() => setAnalyticsFlow(null)} />
    </Stack>
  );
}

export default FlowBuilderListPage;
